import { createResource, createSignal, Match, Show, Switch, type Component } from 'solid-js';

import { Actions } from './components/Actions.js';
import { AgingTable } from './components/AgingTable.js';
import { CompanyDetail } from './components/CompanyDetail.js';
import { Exports } from './components/Exports.js';
import { PlanAndLimits } from './components/PlanAndLimits.js';
import { RemindersToggle } from './components/RemindersToggle.js';
import { Totals } from './components/Totals.js';
import { fetchDashboard } from './lib/api.js';
import type { CompanyAgingRow, VerificationStatus } from './lib/types.js';

/**
 * The AR dashboard (SPEC FR-3): what is outstanding, what is overdue, and who owes it — overdue
 * first, because that is where a merchant's attention belongs.
 *
 * Built from Polaris web components (`s-*`), which Shopify's CDN registers via polaris.js (see
 * index.html — app-bridge.js alone does NOT define them). They are standard custom elements, so
 * this renders natively inside the admin with no React anywhere.
 *
 * The screen is composed from `components/`; this file owns only the data resource, the loading and
 * error states, and which company is open.
 */

/**
 * The "verified" footer is only honest if it reflects a real reconciliation run (SPEC §3.6) — so it
 * reports what actually happened, including when nothing has been verified yet.
 */
function verificationLabel(verification: VerificationStatus): string {
  if (verification.openDiscrepancies > 0) {
    return `${verification.openDiscrepancies} balance${verification.openDiscrepancies === 1 ? '' : 's'} under review — we are on it.`;
  }
  if (!verification.lastVerifiedAt) {
    return 'First verification runs tonight.';
  }
  const when = new Date(verification.lastVerifiedAt);
  const hours = Math.round((Date.now() - when.getTime()) / 3_600_000);
  const ago =
    hours < 1 ? 'just now' : hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
  return `Verified against Shopify ${ago} — all totals reconcile.`;
}

const App: Component = () => {
  const [report, { refetch }] = createResource(fetchDashboard);
  const [openCompany, setOpenCompany] = createSignal<CompanyAgingRow | null>(null);
  /**
   * Bumped on every row click. The modal opens itself imperatively (`showOverlay`), so without a
   * value that changes on each request, dismissing with Esc and then clicking the SAME row again
   * would leave `openCompany` unchanged, re-run no effect, and appear to do nothing.
   */
  const [openToken, setOpenToken] = createSignal(0);

  const open = (company: CompanyAgingRow): void => {
    setOpenCompany(company);
    setOpenToken((token) => token + 1);
  };

  return (
    <s-page heading="Receivables">
      <Switch>
        <Match when={report.loading && report() === undefined}>
          <s-section>
            <s-spinner accessibilityLabel="Loading receivables" />
          </s-section>
        </Match>

        <Match when={report.error}>
          <s-section>
            <s-banner tone="critical" heading="Could not load your receivables">
              <s-paragraph>
                {String(report.error)} — if this keeps happening, email support@duebook.app and a
                human will reply within one business day.
              </s-paragraph>
              <s-button onClick={() => void refetch()}>Try again</s-button>
            </s-banner>
          </s-section>
        </Match>

        <Match when={report()}>
          {(data) => (
            <>
              <Totals report={data()} />

              <Actions onChanged={() => void refetch()} />

              <AgingTable
                report={data()}
                loading={report.loading}
                onOpenCompany={open}
              />

              <Show when={openCompany()}>
                {(company) => (
                  <CompanyDetail
                    company={company()}
                    openToken={openToken()}
                    currency={data().currency}
                    onClose={() => setOpenCompany(null)}
                    onChanged={() => void refetch()}
                  />
                )}
              </Show>

              <RemindersToggle
                settings={data().settings}
                plan={data().plan}
                onChanged={() => void refetch()}
              />

              <Exports plan={data().plan} />

              <PlanAndLimits plan={data().plan} />

              <s-section>
                <s-text tone="subdued">{verificationLabel(data().verification)}</s-text>
              </s-section>
            </>
          )}
        </Match>
      </Switch>

      <s-section>
        <s-text tone="subdued">
          Something off? Email support@duebook.app — we reply within one business day.
        </s-text>
      </s-section>
    </s-page>
  );
};

export default App;
